import { entryMapKey } from "@/lib/entry-key";
import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown,
  ChevronRight,
  Shield,
  PlusCircle,
  Link2,
  Link2Off,
  AlertCircle,
  Camera,
  X,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useToast } from "@/hooks/use-toast";
import type { AssetPolicySubcardContext, PolicyRowData, AssetPolicySuggestion } from "@/../../server/storage";

// Panel fields shown in the expanded view (ordered). username + password excluded — Cat A.
const PANEL_FIELD_KEYS = [
  'type', 'provider', 'policy_num', 'phone', 'premium',
  'beneficiary', 'coverage_amount', 'renewal_date', 'notes',
];

interface VehicleInsuranceSubCardProps {
  clientId: string;
  vehicleSectionId: string;
  vehicleRowId: string;
  vehicleMakeModel?: string;
  vehicleType?: string;
  readOnly?: boolean;
}

const CONTEXT_QUERY_KEY = (clientId: string, vehicleRowId: string) =>
  ['/api/clients', clientId, 'asset-policy-links', vehicleRowId] as const;

// Builds the one-line collapsed summary. Returns hasData=false when provider/policy_num/
// coverage_amount are all empty (triggers amber prompt-to-fill treatment).
function buildCollapsedSummary(fieldValues: Record<string, string>): {
  text: string;
  hasData: boolean;
} {
  const provider = fieldValues['provider']?.trim() ?? '';
  const policyNum = fieldValues['policy_num']?.trim() ?? '';
  const coverage = fieldValues['coverage_amount']?.trim() ?? '';
  const policyType = fieldValues['type']?.trim() ?? '';

  const parts: string[] = [];
  if (provider) parts.push(provider);
  if (policyNum) parts.push(`#${policyNum}`);
  if (coverage) parts.push(coverage);

  if (parts.length > 0) return { text: parts.join(' · '), hasData: true };
  if (policyType) return { text: policyType, hasData: false };
  return { text: 'Insurance policy', hasData: false };
}

function getSuggestionLabel(suggestion: AssetPolicySuggestion): string {
  if (suggestion.policyProvider) return suggestion.policyProvider;
  if (suggestion.policyType) return suggestion.policyType;
  return 'matching policy';
}

export function VehicleInsuranceSubCard({
  clientId,
  vehicleSectionId,
  vehicleRowId,
  vehicleMakeModel,
  vehicleType,
  readOnly = false,
}: VehicleInsuranceSubCardProps) {
  const { toast } = useToast();
  const [expandedPolicyRowIds, setExpandedPolicyRowIds] = useState<Set<string>>(new Set());
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({});

  const contextKey = CONTEXT_QUERY_KEY(clientId, vehicleRowId);

  const { data: ctx, isLoading } = useQuery<AssetPolicySubcardContext>({
    queryKey: contextKey,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientId}/asset-policy-links?vehicleRowId=${vehicleRowId}`);
      if (!res.ok) throw new Error("Failed to load insurance sub-card data");
      return res.json();
    },
    staleTime: Infinity,
  });

  const unlinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const res = await apiRequest("DELETE", `/api/clients/${clientId}/asset-policy-links/${linkId}`);
      if (!res.ok) throw new Error("Failed to unlink policy");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contextKey });
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'asset-policy-links-policy'] });
      toast({ title: "Insurance policy unlinked" });
    },
    onError: (e: Error) => toast({ title: "Unlink failed", description: e.message, variant: "destructive" }),
  });

  // Direct confirm-tap link — used by inline suggestion affordance (no dialog needed)
  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire creates an asset-policy link row.
  const linkFlight = useSingleFlight();

  const confirmSuggestionMutation = useMutation({
    mutationFn: async (policyRowId: string) => {
      if (!ctx) throw new Error("Context not loaded");
      const res = await apiRequest("POST", `/api/clients/${clientId}/asset-policy-links`, {
        vehicleSectionId,
        vehicleRowId,
        policySectionId: ctx.insuranceSectionId,
        policyRowId,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to link");
      }
      return policyRowId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contextKey });
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'asset-policy-links-policy'] });
      toast({ title: "Insurance policy linked" });
    },
    onError: (e: Error) => toast({ title: "Link failed", description: e.message, variant: "destructive" }),
  });

  const upsertEntryMutation = useMutation({
    mutationFn: async ({ fieldId, rowId, value, categoryId, sectionId }: {
      fieldId: string; rowId: string; value: string; categoryId: string; sectionId: string;
    }) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId, sectionId, fieldId, rowId, value,
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contextKey });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const togglePolicyExpanded = useCallback((policyRowId: string) => {
    setExpandedPolicyRowIds(prev => {
      const next = new Set(prev);
      if (next.has(policyRowId)) next.delete(policyRowId);
      else next.add(policyRowId);
      return next;
    });
  }, []);

  const handleFieldBlur = useCallback((fieldId: string, rowId: string) => {
    if (!ctx) return;
    const editKey = entryMapKey(fieldId, rowId);
    const value = localEdits[editKey];
    if (value === undefined) return;
    upsertEntryMutation.mutate({
      fieldId, rowId, value,
      categoryId: ctx.insuranceCategoryId,
      sectionId: ctx.insuranceSectionId,
    });
  }, [ctx, localEdits, upsertEntryMutation]);

  if (isLoading) return null;
  if (!ctx) return null;

  const panelFields = ctx.insuranceFields.filter(f => PANEL_FIELD_KEYS.includes(f.fieldKey));

  // Actionable suggestions — unlinked, and not dismissed by planner
  const actionableSuggestions = ctx.suggestions.filter(
    s => !s.alreadyLinked && !dismissedSuggestions.has(s.policyRowId)
  );

  const isEmpty = ctx.linkedPolicies.length === 0;

  return (
    <div
      className="px-3 py-2"
      style={{ backgroundColor: '#F5F2EC', borderTop: '1px dashed #C8BFA8' }}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-3.5 w-3.5 text-muted-foreground" style={{ flexShrink: 0 }} />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Insurance</span>
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs ml-auto gap-1 text-muted-foreground hover:text-primary"
            onClick={() => setLinkDialogOpen(true)}
          >
            <PlusCircle className="h-3 w-3" />
            Add / Link Insurance
          </Button>
        )}
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div className="rounded border" style={{ borderColor: '#D4CDB8', backgroundColor: '#FDFCFA' }}>
          {/* Inline suggestion — replaces the dead pill entirely */}
          {actionableSuggestions.length > 0 ? (
            <div
              className="flex items-start gap-2.5 px-3 py-2.5"
              style={{ borderBottom: actionableSuggestions.length > 0 ? '1px solid #EBE6DC' : undefined }}
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: '#C8862E' }} />
              <div className="flex-1 min-w-0">
                {actionableSuggestions.map(s => (
                  <div key={s.policyRowId} className="flex items-center justify-between gap-2 mb-1 last:mb-0">
                    <span className="text-xs" style={{ color: '#7A5A2E' }}>
                      Link <strong>"{getSuggestionLabel(s)}"</strong>?
                    </span>
                    {!readOnly && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          className="h-6 text-[11px] px-2.5"
                          style={{ backgroundColor: '#C8862E', color: '#fff', borderColor: 'transparent' }}
                          onClick={() => linkFlight.runQuiet(() => confirmSuggestionMutation.mutateAsync(s.policyRowId))}
                          disabled={linkFlight.inFlight || confirmSuggestionMutation.isPending}
                        >
                          Confirm
                        </Button>
                        <button
                          className="h-6 w-6 flex items-center justify-center rounded hover:bg-black/10"
                          title="Dismiss suggestion"
                          aria-label="Dismiss suggestion"
                          onClick={() => setDismissedSuggestions(prev => new Set(Array.from(prev).concat(s.policyRowId)))}
                        >
                          <X className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Standard empty-state on-ramps */}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <p className="text-xs text-muted-foreground flex-1">No insurance linked to this vehicle.</p>
            {!readOnly && (
              <div className="flex gap-1.5 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <Link2 className="h-3 w-3" />
                  Add insurance
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-muted-foreground"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <Camera className="h-3 w-3" />
                  Photograph policy
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Linked policies */}
      {ctx.linkedPolicies.map(policy => {
        const link = ctx.links.find(l => l.policyRowId === policy.rowId);
        const isExpanded = expandedPolicyRowIds.has(policy.rowId);
        const summary = buildCollapsedSummary(policy.fieldValues);

        return (
          <div
            key={policy.rowId}
            className="rounded border mb-1.5"
            style={{
              backgroundColor: '#FDFCFA',
              borderColor: summary.hasData ? '#D4CDB8' : '#E8B86D',
            }}
          >
            {/* Collapsed summary row — div+role avoids nested-button violation with the unlink control */}
            <div className="flex items-center gap-2 px-3 py-2 rounded hover:bg-black/5 transition-colors">
              <div
                role="button"
                tabIndex={0}
                className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer"
                onClick={() => togglePolicyExpanded(policy.rowId)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePolicyExpanded(policy.rowId); } }}
                aria-expanded={isExpanded}
              >
                {isExpanded
                  ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}

                {summary.hasData ? (
                  <span className="text-sm font-medium text-foreground truncate">{summary.text}</span>
                ) : (
                  <span
                    className="text-xs flex items-center gap-1.5"
                    style={{ color: '#C8862E' }}
                  >
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    Insurance linked — no details yet. Tap to add them.
                  </span>
                )}
              </div>

              {!readOnly && link && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
                  title="Unlink this policy from this vehicle"
                  aria-label="Unlink this policy from this vehicle"
                  onClick={() => unlinkMutation.mutate(link.id)}
                >
                  <Link2Off className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Expanded panel */}
            {isExpanded && (
              <div className="px-4 pb-3 pt-1 border-t" style={{ borderColor: '#D4CDB8' }}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  {panelFields.map(field => {
                    const editKey = entryMapKey(field.id, policy.rowId);
                    const storedValue = policy.fieldValues[field.fieldKey] ?? '';
                    const displayValue = localEdits[editKey] ?? storedValue;
                    const isMultiline = field.fieldType === 'notes' || field.fieldType === 'multiline';
                    const isTel = field.fieldType === 'phone';
                    const isDate = field.fieldType === 'date';

                    return (
                      <div key={field.id} className={isMultiline ? 'col-span-2 sm:col-span-3' : ''}>
                        <label className="block text-[11px] font-semibold text-muted-foreground mb-0.5">
                          {field.name}
                        </label>
                        {isMultiline ? (
                          <Textarea
                            value={displayValue}
                            placeholder={field.placeholder ?? ''}
                            readOnly={readOnly}
                            className="text-sm min-h-[60px] resize-none"
                            onChange={e => setLocalEdits(prev => ({ ...prev, [editKey]: e.target.value }))}
                            onBlur={() => handleFieldBlur(field.id, policy.rowId)}
                          />
                        ) : (
                          <Input
                            value={displayValue}
                            placeholder={field.placeholder ?? ''}
                            readOnly={readOnly}
                            type={isTel ? 'tel' : isDate ? 'date' : 'text'}
                            className="text-sm h-8"
                            onChange={e => setLocalEdits(prev => ({ ...prev, [editKey]: e.target.value }))}
                            onBlur={() => handleFieldBlur(field.id, policy.rowId)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Editing here updates the Insurance Policies record directly.
                  Credentials are managed in the Insurance Policies section.
                </p>
              </div>
            )}
          </div>
        );
      })}

      {/* Additional inline suggestions when policies are already linked */}
      {!isEmpty && actionableSuggestions.length > 0 && !readOnly && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded border mt-1"
          style={{ borderColor: '#E8B86D', backgroundColor: '#FFFBF0' }}
        >
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: '#C8862E' }} />
          <div className="flex-1 space-y-1">
            {actionableSuggestions.map(s => (
              <div key={s.policyRowId} className="flex items-center justify-between gap-2">
                <span className="text-xs" style={{ color: '#7A5A2E' }}>
                  Link <strong>"{getSuggestionLabel(s)}"</strong>?
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    className="h-6 text-[11px] px-2.5"
                    style={{ backgroundColor: '#C8862E', color: '#fff', borderColor: 'transparent' }}
                    onClick={() => linkFlight.runQuiet(() => confirmSuggestionMutation.mutateAsync(s.policyRowId))}
                    disabled={linkFlight.inFlight || confirmSuggestionMutation.isPending}
                  >
                    Confirm
                  </Button>
                  <button
                    className="h-6 w-6 flex items-center justify-center rounded hover:bg-black/10"
                    title="Dismiss suggestion"
                    aria-label="Dismiss suggestion"
                    onClick={() => setDismissedSuggestions(prev => new Set(Array.from(prev).concat(s.policyRowId)))}
                  >
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link / Create dialog */}
      {linkDialogOpen && ctx && (
        <LinkOrCreateDialog
          clientId={clientId}
          vehicleSectionId={vehicleSectionId}
          vehicleRowId={vehicleRowId}
          vehicleMakeModel={vehicleMakeModel}
          vehicleType={vehicleType}
          ctx={ctx}
          onClose={() => setLinkDialogOpen(false)}
          onLinked={() => {
            queryClient.invalidateQueries({ queryKey: contextKey });
            setLinkDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ── Link-or-Create dialog ─────────────────────────────────────────────────────

interface LinkOrCreateDialogProps {
  clientId: string;
  vehicleSectionId: string;
  vehicleRowId: string;
  vehicleMakeModel?: string;
  vehicleType?: string;
  ctx: AssetPolicySubcardContext;
  onClose: () => void;
  onLinked: () => void;
}

function LinkOrCreateDialog({
  clientId,
  vehicleSectionId,
  vehicleRowId,
  vehicleType,
  ctx,
  onClose,
  onLinked,
}: LinkOrCreateDialogProps) {
  const { toast } = useToast();
  const linkedRowIds = new Set(ctx.links.map(l => l.policyRowId));
  const unlinkedPolicies = ctx.allPolicies.filter(p => !linkedRowIds.has(p.rowId));
  const suggestions = ctx.suggestions.filter(s => !s.alreadyLinked);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): both actions below create link rows; one lock, one dialog.
  const linkFlight = useSingleFlight();

  const linkMutation = useMutation({
    mutationFn: async (policyRowId: string) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/asset-policy-links`, {
        vehicleSectionId,
        vehicleRowId,
        policySectionId: ctx.insuranceSectionId,
        policyRowId,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to link");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Insurance policy linked" });
      onLinked();
    },
    onError: (e: Error) => toast({ title: "Link failed", description: e.message, variant: "destructive" }),
  });

  const createAndLinkMutation = useMutation({
    mutationFn: async () => {
      const typeField = ctx.insuranceFields.find(f => f.fieldKey === 'type');
      if (!typeField) throw new Error("Insurance type field not found");

      let defaultType = '';
      if (vehicleType) {
        const vt = vehicleType.toLowerCase();
        if (vt === 'car' || vt === 'truck' || vt === 'suv / minivan') defaultType = 'Auto / Vehicle Insurance';
        else if (vt === 'motorcycle') defaultType = 'Motorcycle Insurance';
        else if (vt === 'boat') defaultType = 'Marine Insurance';
        else if (vt === 'rv / motorhome') defaultType = 'RV Insurance';
      }

      const newRowId = crypto.randomUUID();
      const entryRes = await apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
        categoryId: ctx.insuranceCategoryId,
        sectionId: ctx.insuranceSectionId,
        fieldId: typeField.id,
        rowId: newRowId,
        value: defaultType,
      });
      if (!entryRes.ok) throw new Error("Failed to create insurance entry");

      const linkRes = await apiRequest("POST", `/api/clients/${clientId}/asset-policy-links`, {
        vehicleSectionId,
        vehicleRowId,
        policySectionId: ctx.insuranceSectionId,
        policyRowId: newRowId,
      });
      if (!linkRes.ok) throw new Error("Failed to create link");
      return newRowId;
    },
    onSuccess: () => {
      toast({ title: "New insurance policy created and linked" });
      onLinked();
    },
    onError: (e: Error) => toast({ title: "Create failed", description: e.message, variant: "destructive" }),
  });

  const isSuggested = (policyRowId: string) => suggestions.some(s => s.policyRowId === policyRowId);

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link an Insurance Policy</DialogTitle>
          <DialogDescription>
            Link an existing policy or create a new one for this vehicle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
          {suggestions.length > 0 && (
            <div className="flex items-start gap-2 p-2.5 rounded-md" style={{ backgroundColor: '#FFFBF0', border: '1px solid #E8B86D' }}>
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#C8862E' }} />
              <p className="text-xs" style={{ color: '#7A5A2E' }}>
                {suggestions.length === 1
                  ? `A policy matching this vehicle type is highlighted below — confirm with one tap.`
                  : `${suggestions.length} policies may match this vehicle type — they're highlighted below.`}
              </p>
            </div>
          )}

          {unlinkedPolicies.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Existing policies</p>
              {unlinkedPolicies.map(policy => {
                const suggested = isSuggested(policy.rowId);
                const summary = buildCollapsedSummary(policy.fieldValues);
                return (
                  <div
                    key={policy.rowId}
                    className="flex items-center justify-between p-2.5 rounded border gap-2"
                    style={{
                      borderColor: suggested ? '#E8B86D' : '#D4CDB8',
                      backgroundColor: suggested ? '#FFFBF0' : '#FDFCFA',
                    }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{summary.text}</p>
                      {suggested && (
                        <p className="text-[10px] mt-0.5" style={{ color: '#C8862E' }}>
                          Matches vehicle type — tap to confirm link
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={suggested ? "default" : "outline"}
                      className="shrink-0 h-7 text-xs"
                      style={suggested ? { backgroundColor: '#C8862E', borderColor: 'transparent' } : {}}
                      onClick={() => linkFlight.runQuiet(() => linkMutation.mutateAsync(policy.rowId))}
                      disabled={linkMutation.isPending}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Link
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {unlinkedPolicies.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              All existing insurance policies are already linked to this vehicle.
            </p>
          )}
        </div>

        <DialogFooter className="flex items-center gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="mr-auto">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => linkFlight.runQuiet(() => createAndLinkMutation.mutateAsync())}
            disabled={createAndLinkMutation.isPending}
            className="gap-1.5"
          >
            <PlusCircle className="h-4 w-4" />
            Create new policy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
